import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchLikeInfo, addLike, removeLike } from "../lib/likesApi";

// Public Like control with a visible count. `card` = compact overlay pill for event
// cards; `detail` = full control for the event detail page. Logged-out users see the
// count and are sent to the existing /login flow if they try to Like. Distinct from
// the Save/bookmark control (which stays private and unchanged).
export const LikeButton = ({ eventId, variant = "card" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    fetchLikeInfo(eventId, user?.id)
      .then((info) => {
        if (mounted.current) {
          setCount(info.count);
          setLiked(info.liked);
        }
      })
      .catch(() => {});
    return () => {
      mounted.current = false;
    };
  }, [eventId, user?.id]);

  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      navigate("/login");
      return;
    }
    if (busy) return;
    setBusy(true);
    const nextLiked = !liked;
    setLiked(nextLiked);
    setCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    try {
      if (nextLiked) await addLike(eventId, user.id);
      else await removeLike(eventId, user.id);
      const info = await fetchLikeInfo(eventId, user.id);
      if (mounted.current) {
        setCount(info.count);
        setLiked(info.liked);
      }
    } catch {
      if (mounted.current) {
        setLiked(!nextLiked);
        setCount((c) => Math.max(0, c + (nextLiked ? -1 : 1)));
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (variant === "detail") {
    return (
      <button
        type="button"
        data-testid={`like-btn-${eventId}`}
        onClick={onClick}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-[#121212] py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1e1e1e] active:scale-[0.98]"
      >
        <Heart
          className={`h-[18px] w-[18px] ${liked ? "fill-[#ff2a00] text-[#ff2a00]" : "text-[#ff2a00]"}`}
        />
        {liked ? "Liked" : "Like"}
        <span data-testid={`like-count-${eventId}`} className="text-white/60">· {count}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={`like-btn-${eventId}`}
      onClick={onClick}
      aria-label={liked ? "Unlike event" : "Like event"}
      className="absolute bottom-3 right-3 z-10 flex items-center gap-1 rounded-full bg-[#050505]/85 px-2.5 py-1 text-[11px] font-bold text-white transition-transform duration-200 active:scale-90"
    >
      <Heart
        className={`h-3.5 w-3.5 transition-colors ${liked ? "fill-[#ff2a00] text-[#ff2a00]" : "text-white"}`}
      />
      <span data-testid={`like-count-${eventId}`}>{count}</span>
    </button>
  );
};
